import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { tenantsAPI } from '../services/api';

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  authMode: 'FULL' | 'EMAIL_ONLY';
  color: string | null;
  logoUrl: string | null;
}

interface TenantContextType {
  tenant: Tenant | null;
  tenantSlug: string;
  isEmailOnly: boolean;
  loading: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

function resolveSlugFromPath(pathname: string): string {
  return 'najsport';
}

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  const tenantSlug = resolveSlugFromPath(location.pathname);

  useEffect(() => {
    // Persist slug so the axios interceptor can read it
    localStorage.setItem('tenantSlug', tenantSlug);

    setLoading(true);
    tenantsAPI
      .getBySlug(tenantSlug)
      .then((data) => setTenant(data))
      .catch(() => setTenant(null))
      .finally(() => setLoading(false));
  }, [tenantSlug]);

  const value = useMemo<TenantContextType>(
    () => ({
      tenant,
      tenantSlug,
      isEmailOnly: tenant?.authMode === 'EMAIL_ONLY',
      loading,
    }),
    [tenant, tenantSlug, loading],
  );

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
